import { DateTime } from 'luxon';

import { fireflyAuthenticatedApi } from '@/app/lib/axios';
import { BankAccountType } from '@/app/network/models/bank-account-type';
import { AccountDetails } from '@/app/network/models/v1/account-details';
import { Bill } from '@/app/network/models/v1/bill';
import { MakePayment } from '@/app/network/models/v1/make-payment';
import { Notification } from '@/app/network/models/v1/notification';
import { NotificationPreference } from '@/app/network/models/v1/notification-preference';
import { OnboardingRequirement } from '@/app/network/models/v1/onboarding-requirement';
import { Payment } from '@/app/network/models/v1/payment';
import { PaymentMethod } from '@/app/network/models/v1/payment-method';
import { Telemetry } from '@/app/network/models/v1/telemetry';
import { TelemetryInterval } from '@/app/network/models/v1/telemetry-interval';
import { Temperature } from '@/app/network/models/v1/temperature';
import { CurrentUserResponse } from '@/app/network/models/v1/user';
import { AuthorizedUsers } from '@/app/network/models/v2/authorized-users';
import { PaymentMethodRequest } from '@/billing/models/payments';

export async function addAuthorizedUser(
  userId: number,
  emailAddress: string,
  memberNumbers: number[],
  accountNumbers: number[]
): Promise<void> {
  return await fireflyAuthenticatedApi.post(
    `v2/users/${userId}/authorized-users`,
    {
      target_email: emailAddress,
      member_numbers: memberNumbers,
      account_numbers: accountNumbers
    }
  );
}

export async function addPaymentMethodFromCard(
  userId: number,
  nameOnCard: string,
  cardNumber: string,
  expirationMonth: number,
  expirationYear: number,
  cvv: string,
  addressLine1: string,
  addressLine2: string | undefined,
  city: string,
  state: string,
  zip: string,
  alias?: string
): Promise<void> {
  return await fireflyAuthenticatedApi.post(
    `v1/users/${userId}/payment-methods`,
    {
      alias: alias === undefined || alias === '' ? null : alias,
      card_details: {
        name_on_card: nameOnCard,
        card_number: cardNumber,
        expiration_month: expirationMonth,
        expiration_year: expirationYear,
        cvv: cvv,
        billing_address: {
          line1: addressLine1,
          line2: addressLine2,
          city: city,
          state: state,
          zip: zip,
          country: 'US'
        }
      },
      check_details: null
    }
  );
}

export async function addPaymentMethodFromCheck(
  userId: number,
  nameOnAccount: string,
  routingNumber: string,
  accountNumber: string,
  accountType: BankAccountType,
  alias?: string
): Promise<void> {
  return await fireflyAuthenticatedApi.post(
    `v1/users/${userId}/payment-methods`,
    {
      alias: alias === undefined || alias === '' ? null : alias,
      card_details: null,
      check_details: {
        name_on_account: nameOnAccount,
        account_number: accountNumber,
        account_type: accountType,
        routing_number: routingNumber
      }
    }
  );
}

export async function confirmSmsVerificationCode(
  userId: number,
  number: string,
  code: number
): Promise<void> {
  return await fireflyAuthenticatedApi.post(
    `v1/users/${userId}/sms-number`,
    {
      phone_number: number,
      verification_code: code
    }
  );
}

export async function deleteAutomaticPayment(accountNumber: number): Promise<void> {
  return await fireflyAuthenticatedApi.delete(
    `v1/accounts/${accountNumber}/automatic-payment`
  );
}

export async function deletePaymentMethod(userId: number, id: string): Promise<void> {
  return await fireflyAuthenticatedApi.delete(
    `v1/users/${userId}/payment-methods/${id}`
  );
}

export async function getAccountDetails(accountNumber: number): Promise<AccountDetails> {
  return await fireflyAuthenticatedApi.get(
    `v1/accounts/${accountNumber}`,
    {
      params: {
        fields: 'outage_status'
      }
    }
  );
}

export async function getAccountNotificationPreferences(userId: number, accountNumber: number): Promise<NotificationPreference[]> {
  return await fireflyAuthenticatedApi.get(
    `v1/users/${userId}/accounts/${accountNumber}/notification-preferences`
  );
}

/**
 * Returns the notification preferences for a given user.
 */
export async function getNotificationPreferences(userId: number): Promise<NotificationPreference[]> {
  return await fireflyAuthenticatedApi.get(
    `v1/users/${userId}/notification-preferences`
  );
}

/**
 * Returns temperatures for an account between the specified range
 * (inclusive). Results are provided in the specified interval.
 */
export async function getAccountTemperatures(accountNumber: number, from: DateTime, to: DateTime, interval: 'daily' | 'monthly' | 'yearly'): Promise<Temperature[]> {
  return await fireflyAuthenticatedApi.get(
    `v1/accounts/${accountNumber}/temperatures`,
    {
      params: {
        from: from.toISODate(),
        to: to.toISODate(),
        interval: interval
      }
    }
  );
}

export async function getAccountUsage(accountNumber: number, startDate: DateTime, endDate: DateTime, interval: TelemetryInterval): Promise<Telemetry[]> {
  return await fireflyAuthenticatedApi.get(
    `v1/accounts/${accountNumber}/usage`,
    {
      params: {
        start_date: startDate.toISO(),
        end_date: endDate.toISO(),
        interval: interval
      }
    }
  );
}

export async function getAuthorizedUsers(userId: number): Promise<AuthorizedUsers> {
  return await fireflyAuthenticatedApi.get(
    `v2/users/${userId}/authorized-users`
  );
}

/**
 * Returns the billing history for an account between the start and end
 * dates (inclusive).
 */
export async function getBillingHistory(accountNumber: number, startDate: DateTime, endDate: DateTime): Promise<Bill[]> {
  return await fireflyAuthenticatedApi.get(
    `v1/accounts/${accountNumber}/billing-history`,
    {
      params: {
        start_date: startDate.toISODate(),
        end_date: endDate.toISODate()
      }
    }
  );
}

export async function getNotifications(userId: number): Promise<Notification[]> {
  return await fireflyAuthenticatedApi.get(
    `v1/users/${userId}/notifications`
  );
}

export async function getOnboardingRequirement(userId: number): Promise<OnboardingRequirement> {
  return await fireflyAuthenticatedApi.get(
    `v1/users/${userId}/onboarding`
  );
}

export async function getPaperlessBillingSetting(memberNumber: number): Promise<{ value: boolean }> {
  return await fireflyAuthenticatedApi.get(
    `v1/memberships/${memberNumber}/paperless-billing`
  );
}

export async function getPaymentHistory(accountNumber: number): Promise<Payment[]> {
  return await fireflyAuthenticatedApi.get(
    `v1/accounts/${accountNumber}/payment-history`
  );
}

export async function getPaymentMethods(userId: number): Promise<PaymentMethod[]> {
  return await fireflyAuthenticatedApi.get(
    `v1/users/${userId}/payment-methods`
  );
}

export async function getTapEnrollmentSetting(accountNumber: number): Promise<{ value: boolean }> {
  return await fireflyAuthenticatedApi.get(
    `v1/accounts/${accountNumber}/tap-enrollment`
  );
}

export async function getUser(): Promise<CurrentUserResponse> {
  return await fireflyAuthenticatedApi.get(
    'v1/users/current'
  );
}

export async function linkMembership(
  userId: number,
  memberNumber: number,
  memberPin: string,
  alias?: string
): Promise<void> {
  return await fireflyAuthenticatedApi.post(
    `v1/users/${userId}/memberships`,
    {
      member_number: memberNumber,
      pin: memberPin,
      alias: alias
    }
  );
}

export async function makePayment(userId: number, data: MakePayment): Promise<void> {
  return await fireflyAuthenticatedApi.post(
    `v1/users/${userId}/make-payment`,
    data
  );
}

export async function removeAuthorizedUser(userId: number, id: number): Promise<void> {
  return await fireflyAuthenticatedApi.delete(
    `v2/users/${userId}/authorized-users/${id}`
  );
}

export async function removeOnboardingRequirement(userId: number): Promise<void> {
  return await fireflyAuthenticatedApi.delete(
    `v1/users/${userId}/onboarding`
  );
}

export async function removePendingInvite(userId: number, id: number): Promise<void> {
  return await fireflyAuthenticatedApi.delete(
    `v2/users/${userId}/authorized-users/${id}/pending`
  );
}

export async function requestSmsVerificationCode(userId: number, number: string): Promise<void> {
  return await fireflyAuthenticatedApi.post(
    `v1/users/${userId}/sms-code`,
    {
      phone_number: number,
      verification_code: null
    }
  );
}

export async function saveAccountAlias(userId: number, accountNumber: number, alias?: string): Promise<void> {
  return await fireflyAuthenticatedApi.put(
    `v1/users/${userId}/accounts/${accountNumber}`,
    {
      alias: alias
    }
  );
}

export async function saveAccountNotificationPreferences(userId: number, accountNumber: number, preferencesToSave: any): Promise<void> {
  return await fireflyAuthenticatedApi.put(
    `v1/users/${userId}/accounts/${accountNumber}/notification-preferences`,
    preferencesToSave
  );
}

export async function saveNotificationPreferences(userId: number, email: boolean, push: boolean): Promise<void> {
  let selectedDeliveryMethods = [];
  if (email) selectedDeliveryMethods.push('email');
  if (push) selectedDeliveryMethods.push('push');

  return await fireflyAuthenticatedApi.put(
    `v1/users/${userId}/notification-preferences`,
    [
      {
        notification_category: 'announcements',
        selected_delivery_methods: selectedDeliveryMethods
      }
    ]
  );
}

export async function saveMembershipAlias(userId: number, membershipNumber: number, alias?: string): Promise<void> {
  return await fireflyAuthenticatedApi.put(
    `v1/users/${userId}/memberships/${membershipNumber}`,
    {
      alias: alias
    }
  );
}

export async function setupAutomaticPayment(accountNumber: number, data: PaymentMethodRequest): Promise<void> {
  return await fireflyAuthenticatedApi.post(
    `v1/accounts/${accountNumber}/automatic-payment`,
    data
  );
}

export async function unlinkAccount(userId: number, accountNumber: number): Promise<void> {
  return await fireflyAuthenticatedApi.delete(
    `v1/users/${userId}/accounts/${accountNumber}`
  );
}

export async function unlinkMembership(userId: number, memberNumber: number): Promise<void> {
  return await fireflyAuthenticatedApi.delete(
    `v1/users/${userId}/memberships/${memberNumber}`
  );
}

export async function updatePaperlessBillingSetting(memberNumber: number, preference: boolean): Promise<void> {
  return await fireflyAuthenticatedApi.put(
    `v1/memberships/${memberNumber}/paperless-billing`,
    {
      value: preference
    }
  );
}

export async function updatePaymentMethod(userId: number, paymentId: string, newAlias: string): Promise<void> {
  return await fireflyAuthenticatedApi.put(
    `v1/users/${userId}/payment-methods/${paymentId}`,
    {
      alias: newAlias
    }
  );
}

export async function updateProfile(
  userId: number,
  firstName: string,
  lastName: string,
  language: string,
  newsletter: boolean
): Promise<void> {
  // TODO: confirm this handles an empty 200 OK
  return await fireflyAuthenticatedApi.put(
    `v1/users/${userId}`,
    {
      first_name: firstName,
      last_name: lastName,
      language: language,
      newsletter: newsletter
    }
  );
}

export async function updateTapEnrollmentSetting(accountNumber: number, preference: boolean): Promise<void> {
  return await fireflyAuthenticatedApi.put(
    `v1/accounts/${accountNumber}/tap-enrollment`,
    {
      value: preference
    }
  );
}

export async function reportOutage({ accountNumber }: { accountNumber: number }) {
  return await fireflyAuthenticatedApi.post(`v1/accounts/${accountNumber}/report-outage`);
}

export async function updateEmail(userId: number, oldEmail: string, newEmail: string): Promise<void> {
  return await fireflyAuthenticatedApi.post(
    `v1/users/${userId}/change-email`,
    {
      old_email: oldEmail,
      new_email: newEmail,
      // sb: this is still validated as not-null by the API but is no longer
      // validated to match. remove this once the API no longer validates its
      // existence.
      confirm_email: newEmail
    }
  );
}

export async function updatePassword(userId: number, currentPassword: string, newPassword: string): Promise<void> {
  return await fireflyAuthenticatedApi.post(
    `v1/users/${userId}/change-password`,
    {
      old_password: currentPassword,
      new_password: newPassword,
      // sb: this is still validated as not-null by the API but is no longer
      // validated to match. remove this once the API no longer validates its
      // existence.
      confirm_password: newPassword
    }
  );
}
